import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  MessageSquare, 
  Loader2, 
  CreditCard,
  Settings,
  Receipt,
  Calendar,
  Check,
  X,
  AlertTriangle,
  ArrowLeft,
  ExternalLink,
  RefreshCw
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SubscriptionManagePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [billingHistory, setBillingHistory] = useState([]);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [subResponse, historyResponse] = await Promise.all([
        axios.get(`${API}/subscription/status`),
        axios.get(`${API}/subscription/billing-history`)
      ]);
      setSubscription(subResponse.data);
      setBillingHistory(historyResponse.data.transactions || []);
    } catch (error) {
      console.error('Failed to fetch subscription data:', error);
      toast.error('Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  const openBillingPortal = async () => {
    setActionLoading('portal');
    try {
      const response = await axios.post(`${API}/subscription/create-portal-session`);
      window.location.href = response.data.portal_url;
    } catch (error) {
      console.error('Portal error:', error);
      toast.error('Failed to open billing portal');
      setActionLoading(null);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will still have access until the end of your current billing period.')) {
      return;
    }
    
    setActionLoading('cancel');
    try {
      await axios.post(`${API}/subscription/cancel`);
      toast.success('Subscription will be canceled at the end of your billing period');
      fetchData();
    } catch (error) {
      console.error('Cancel error:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReactivate = async () => {
    setActionLoading('reactivate');
    try {
      await axios.post(`${API}/subscription/reactivate`);
      toast.success('Subscription reactivated successfully');
      fetchData();
    } catch (error) {
      console.error('Reactivate error:', error);
      toast.error(error.response?.data?.detail || 'Failed to reactivate subscription');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount, currency = 'usd') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      active: 'bg-emerald-100 text-emerald-700',
      trialing: 'bg-blue-100 text-blue-700',
      canceled: 'bg-red-100 text-red-700',
      trial_expired: 'bg-amber-100 text-amber-700',
      none: 'bg-slate-100 text-slate-700',
      paid: 'bg-emerald-100 text-emerald-700',
      pending: 'bg-amber-100 text-amber-700',
      failed: 'bg-red-100 text-red-700'
    };
    
    const statusLabels = {
      active: 'Active',
      trialing: 'Free Trial',
      canceled: 'Canceled',
      trial_expired: 'Trial Expired',
      none: 'No Subscription',
      paid: 'Paid',
      pending: 'Pending',
      failed: 'Failed'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusStyles[status] || statusStyles.none}`}>
        {statusLabels[status] || status}
      </span>
    );
  };

  const isActive = subscription?.status === 'active' || subscription?.status === 'trialing';

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-teal-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-teal-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/dashboard')}
                data-testid="back-to-dashboard-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  IntakePal
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Subscription Management
        </h1>

        <div className="grid gap-6">
          {/* Current Plan Card */}
          <Card data-testid="current-plan-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-teal-500" />
                    Current Plan
                  </CardTitle>
                  <CardDescription>Your subscription details</CardDescription>
                </div>
                {getStatusBadge(subscription?.status)}
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500 mb-1">Plan</p>
                  <p className="text-lg font-semibold text-slate-900">
                    {subscription?.plan === 'pro' ? 'IntakePal Pro' : 'Free'}
                  </p>
                  <p className="text-sm text-slate-600">$179.99/month</p>
                </div>
                
                {subscription?.trial_ends_at && subscription?.status === 'trialing' && (
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-600 mb-1">Trial Ends</p>
                    <p className="text-lg font-semibold text-blue-900">
                      {formatDate(subscription.trial_ends_at)}
                    </p>
                    <p className="text-sm text-blue-600">
                      {Math.ceil((new Date(subscription.trial_ends_at) - new Date()) / (1000 * 60 * 60 * 24))} days remaining
                    </p>
                  </div>
                )}

                {subscription?.current_period_end && subscription?.status === 'active' && (
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500 mb-1">Next Billing Date</p>
                    <p className="text-lg font-semibold text-slate-900">
                      {formatDate(subscription.current_period_end)}
                    </p>
                  </div>
                )}
              </div>

              {/* Cancellation Warning */}
              {subscription?.cancel_at_period_end && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">Subscription Scheduled for Cancellation</p>
                    <p className="text-sm text-amber-700 mt-1">
                      Your subscription will end on {formatDate(subscription.trial_ends_at || subscription.current_period_end)}. 
                      You can reactivate anytime before then.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-3 border-amber-500 text-amber-700 hover:bg-amber-100"
                      onClick={handleReactivate}
                      disabled={actionLoading === 'reactivate'}
                      data-testid="reactivate-btn"
                    >
                      {actionLoading === 'reactivate' ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-2" />
                      )}
                      Reactivate Subscription
                    </Button>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 pt-4 border-t border-slate-200">
                <Button
                  onClick={openBillingPortal}
                  disabled={actionLoading === 'portal'}
                  className="bg-teal-500 hover:bg-teal-600"
                  data-testid="manage-billing-btn"
                >
                  {actionLoading === 'portal' ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Settings className="w-4 h-4 mr-2" />
                  )}
                  Manage Billing
                  <ExternalLink className="w-3 h-3 ml-2" />
                </Button>
                
                {isActive && !subscription?.cancel_at_period_end && (
                  <Button
                    variant="outline"
                    onClick={handleCancelSubscription}
                    disabled={actionLoading === 'cancel'}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    data-testid="cancel-subscription-btn"
                  >
                    {actionLoading === 'cancel' ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <X className="w-4 h-4 mr-2" />
                    )}
                    Cancel Subscription
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Billing History Card */}
          <Card data-testid="billing-history-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="w-5 h-5 text-teal-500" />
                Billing History
              </CardTitle>
              <CardDescription>Your recent transactions</CardDescription>
            </CardHeader>
            <CardContent>
              {billingHistory.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Receipt className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No billing history yet</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {billingHistory.map((tx, index) => (
                    <div 
                      key={tx.id || index} 
                      className="py-4 flex items-center justify-between"
                      data-testid={`transaction-${index}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          tx.status === 'paid' ? 'bg-emerald-100' : 'bg-slate-100'
                        }`}>
                          {tx.status === 'paid' ? (
                            <Check className="w-5 h-5 text-emerald-600" />
                          ) : (
                            <Calendar className="w-5 h-5 text-slate-500" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">
                            IntakePal Pro - Monthly
                          </p>
                          <p className="text-sm text-slate-500">
                            {formatDate(tx.date)}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-slate-900">
                          {formatCurrency(tx.amount, tx.currency)}
                        </p>
                        {getStatusBadge(tx.status)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Help Card */}
          <Card className="border-teal-200 bg-teal-50/50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Need Help?</h3>
                  <p className="text-sm text-slate-600 mb-3">
                    Have questions about your subscription or billing? Our support team is here to help.
                  </p>
                  <a 
                    href="mailto:support@intakepal.com" 
                    className="text-sm font-medium text-teal-600 hover:text-teal-700"
                  >
                    Contact support@intakepal.com
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default SubscriptionManagePage;
